import { Anchor, Col, Row, Tabs as AntTabs, ConfigProvider } from "antd";
import { Overview } from "./providerDetails/overview";
import PersonalInfo from "./providerDetails/personalInfo";
import ProfessionalInfo from "./providerDetails/professionalInfo";
import IncredableContent from "../components/incredable-content";
import { SpaceBetween } from "@cloudscape-design/components";
import AffiliationsV2 from "./providerDetails/affiliations-v2";
import DEAV2 from "./providerDetails/dea-v2";
import EducationV2 from "./providerDetails/education-v2";
import LiabilityInsuranceV2 from "./providerDetails/liabilityInsurance-v2";
import LicenseV2 from "./providerDetails/license-v2";
import PeerReferenceV2 from "./providerDetails/peerreference-v2";
import SpecialtyInfoV2 from "./providerDetails/specialtyInfo-v2";
import ThirdPartyLoginV2 from "./providerDetails/thirdpartylogin-v2";
import WorkHistoryV2 from "./providerDetails/workhistory-v2";
import EnrolledPayerV2 from "./providerDetails/enrolledpayer-v2";
import SafeDocumentsList from "../safe/safe-documents-list";
import CMETrackerList from "./providerDetails/cme-tracker-list";
import Comments from "./providerDetails/comments";
import SmsMessages from "./sms_messages";
import SectionTableList from "./profileBuilder/section-table";
import ProviderProfileAuditLog from "./provider-audit-logs";
import SanctionCheckScreen from "../components/SanctionCheckScreen";
import { useGetAllSectionQuery } from "../redux/api/section/section";
import { useGetProviderCustomProfileQuery } from "../redux/api/provider/provider";
import SectionCard from "./profileBuilder/sectionCard";
import { useLocation, useParams } from "react-router-dom";
import { useGetOrganizationDetailsQuery } from "../redux/api/organization/organization";
function AnchorLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{ fontSize: "14px", fontWeight: "500", letterSpacing: ".25px" }}
    >
      {children}
    </span>
  );
}

export default function ProviderDetails() {
  const { providerId } = useParams();
  const location = useLocation();
  // const queryParams = new URLSearchParams(location.search);
  // const includeStatic = queryParams.get("includeStatic") === "true";
  const { data: organization } = useGetOrganizationDetailsQuery();
  const migratedToDynamicProfiles =
    organization?.settings?.migratedToDynamicProfiles;
  const { data: sectionData, isLoading: sectionLoading } =
    useGetAllSectionQuery({ isFacility: false });
  const { data: providerCustomProfile = {} } = useGetProviderCustomProfileQuery(
    {
      providerId: String(providerId),
      pageNumber: 0,
      pageSize: 5,
    }
  );
  return (
    <IncredableContent spinning={sectionLoading}>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              itemSelectedColor: "#2B6BE6",
              cardBg: "#F5F5F5",
              colorBgContainer: "#2B6BE6",
              inkBarColor: "#2B6BE6",
              colorBorderSecondary: "#c6c6cd",
              fontSize: 16,
            },
          },
        }}
      >
        <div style={{ position: "relative" }}>
          <AntTabs
            defaultActiveKey="details"
            size="large"
            items={[
              {
                label: <span style={{ fontWeight: "700" }}>Details</span>,
                key: "details",
                children: (
                  <Row gutter={12}>
                    <Col span={19}>
                      <SpaceBetween size="m">
                        {/* Always at top */}
                        <div id="overview">
                          <Overview />
                        </div>

                        {/* Static or dynamic middle sections */}
                        {!migratedToDynamicProfiles ? (
                          <>
                            <div id="personalinfo">
                              <PersonalInfo />
                            </div>
                            <div id="professionalinfo">
                              <ProfessionalInfo />
                            </div>
                            <div id="dea">
                              <DEAV2 />
                            </div>
                            <div id="education">
                              <EducationV2 />
                            </div>
                            <div id="liabilityinsurance">
                              <LiabilityInsuranceV2 />
                            </div>
                            <div id="license">
                              <LicenseV2 />
                            </div>
                            <div id="peerreference">
                              <PeerReferenceV2 />
                            </div>
                            <div id="specialtyinfo">
                              <SpecialtyInfoV2 />
                            </div>
                            <div id="safe">
                              <SafeDocumentsList addButtonLabel="Add" />
                            </div>
                            <div id="workhistory">
                              <WorkHistoryV2 />
                            </div>
                            <div id="cme-tracker">
                              <CMETrackerList />
                            </div>
                          </>
                        ) : (
                          sectionData
                            ?.filter((section) => section?.active)
                            ?.map((section) => {
                              const sectionRecords =
                                providerCustomProfile[
                                section.id as keyof typeof providerCustomProfile
                                ] || [];
                              return (
                                <div id={`${section?.name}`} key={section?.id}>
                                  {section?.allowMultiple ? (
                                    <SectionTableList
                                      sectionMeta={section}
                                      sectionData={sectionRecords}
                                    />
                                  ) : (
                                    <SectionCard
                                      sectionMeta={section}
                                      sectionData={sectionRecords}
                                    />
                                  )}
                                </div>
                              );
                            })
                        )}

                        {/* Always at bottom */}
                        <div id="affiliation">
                          <AffiliationsV2 />
                        </div>
                        <div id="enrolledpayer">
                          <EnrolledPayerV2 />
                        </div>
                        <div id="thirdpartylogin">
                          <ThirdPartyLoginV2 />
                        </div>
                        <div id="safe">
                          <SafeDocumentsList addButtonLabel="Add" />
                        </div>
                      </SpaceBetween>
                    </Col>
                    <Col span={5}>
                      <Anchor
                        affix={true}
                        targetOffset={100}
                        offsetTop={100}
                        style={{ overflowY: "scroll", scrollbarWidth: "none" }}
                        items={[
                          // 1. Always at top
                          {
                            key: "overview",
                            href: "#overview",
                            title: (
                              <AnchorLabel>General Information</AnchorLabel>
                            ),
                          },

                          // 2. Conditional middle items
                          ...(!migratedToDynamicProfiles
                            ? [
                              {
                                key: "personalinfo",
                                href: "#personalinfo",
                                title: (
                                  <AnchorLabel>Personal Info</AnchorLabel>
                                ),
                              },
                              {
                                key: "professionalinfo",
                                href: "#professionalinfo",
                                title: (
                                  <AnchorLabel>Professional Info</AnchorLabel>
                                ),
                              },
                              {
                                key: "dea",
                                href: "#dea",
                                title: <AnchorLabel>DEA</AnchorLabel>,
                              },
                              {
                                key: "education",
                                href: "#education",
                                title: <AnchorLabel>Education</AnchorLabel>,
                              },
                              {
                                key: "liabilityinsurance",
                                href: "#liabilityinsurance",
                                title: (
                                  <AnchorLabel>
                                    Liability Insurance
                                  </AnchorLabel>
                                ),
                              },
                              {
                                key: "license",
                                href: "#license",
                                title: <AnchorLabel>License</AnchorLabel>,
                              },
                              {
                                key: "peerreference",
                                href: "#peerreference",
                                title: (
                                  <AnchorLabel>Peer Reference</AnchorLabel>
                                ),
                              },
                              {
                                key: "specialtyinfo",
                                href: "#specialtyinfo",
                                title: (
                                  <AnchorLabel>Specialty Info</AnchorLabel>
                                ),
                              },
                              {
                                key: "workhistory",
                                href: "#workhistory",
                                title: (
                                  <AnchorLabel>Work History</AnchorLabel>
                                ),
                              },
                              {
                                key: "cme-tracker",
                                href: "#cme-tracker",
                                title: <AnchorLabel>CME Tracker</AnchorLabel>,
                              },
                            ]
                            : sectionData
                              ?.filter((section) => section?.active)
                              ?.map((section) => ({
                                key: `${section?.id}`,
                                href: `#${section?.name}`,
                                title: (
                                  <AnchorLabel>
                                    <span
                                      style={{ textTransform: "capitalize" }}
                                    >
                                      {section?.name}
                                    </span>
                                  </AnchorLabel>
                                ),
                              })) || []),

                          // 3. Always at bottom
                          {
                            key: "affiliations",
                            href: "#affiliation",
                            title: <AnchorLabel>Affiliations</AnchorLabel>,
                          },
                          {
                            key: "enrolledpayer",
                            href: "#enrolledpayer",
                            title: <AnchorLabel>Enrolled Payers</AnchorLabel>,
                          },
                          {
                            key: "thirdpartylogin",
                            href: "#thirdpartylogin",
                            title: <AnchorLabel>Third Party Login</AnchorLabel>,
                          },
                          {
                            key: "safe",
                            href: "#safe",
                            title: <AnchorLabel>Safe</AnchorLabel>,
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                ),
              },
              {
                label: <span style={{ fontWeight: "700" }}>Comments</span>,
                key: "comments",
                children: <Comments />,
              },
              {
                label: <span style={{ fontWeight: "700" }}>Sanctions</span>,
                key: "sanctions",
                children: <SanctionCheckScreen />,
              },
              {
                label: <span style={{ fontWeight: "700" }}>Audit logs</span>,
                key: "auditLog",
                children: <ProviderProfileAuditLog />,
              },
              {
                label: <span style={{ fontWeight: "700" }}>Message</span>,
                key: "messages",
                children: <SmsMessages />,
              },
            ]}
          />
        </div>
      </ConfigProvider>
    </IncredableContent>
  );
}
